import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import createSafeContext from '#/utils/createSafeContext';

import { useWalletView } from '#/features/wallet/wallet-context';

import { Account, getAccount } from '#/api/account';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';
import { AccountNotification } from '#/api/ws/ws';

import {
  AccountView,
  accountValueView,
  freeCollateralView,
  healthFactorView,
  statusView,
  EMPTY_VIEW,
} from './account-views';

const INITIAL_STATE: AccountState = {
  status: 'connected',
  error: '',
  account: null,
};

export interface AccountState extends LiveConnectionBaseState {
  readonly account: Account | null;
}

export const [useAccountState, AccountStateCtx] =
  createSafeContext<AccountState>('AccountState');

export const [useAccountView, AccountViewCtx] =
  createSafeContext<AccountView>('AccountView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function AccountProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const view = useMemo(() => createView(state), [state]);

  useAccountFetch(setState);

  return (
    <AccountStateCtx value={state}>
      <AccountViewCtx value={view}>{props.children}</AccountViewCtx>
    </AccountStateCtx>
  );
}

export function createView(state: AccountState): AccountView {
  const { account } = state;

  if (account == null) return EMPTY_VIEW;

  const status = statusView(account);

  const freeCollateral = freeCollateralView(account);

  const accountValue = accountValueView(account);

  const healthFactor = healthFactorView(account);

  const isBeingLiquidated = account.status === 'LIQUIDATION';

  return {
    status,
    freeCollateral,
    accountValue,
    healthFactor,
    isBeingLiquidated,
  };
}

function useAccountFetch(
  setState: Dispatch<SetStateAction<AccountState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;
  const channel = `account`;

  const fetchAll = useCallback(
    async (signal: AbortSignal) => getAccount({ signal }),
    [],
  );

  const setAll = useCallback(
    (data: Account) => {
      setState((state) => ({
        ...state,
        account: data,
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      account: INITIAL_STATE.account,
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: AccountNotification) => {
      setState((state) => ({
        ...state,
        account: notif.data,
      }));
    },
    [setState],
  );

  useLiveConnection<Account, AccountState, AccountNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
